import React, { useState, useEffect } from 'react';
import * as osTemplateService from '../../services/osTemplateService';
import * as proxmoxHostService from '../../services/proxmoxHostService';
import type {
  OSTemplate,
  HostTemplate,
  TemplateStats,
  CreateOSTemplateData
} from '../../services/osTemplateService';
import type { ProxmoxHost } from '../../services/proxmoxHostService';

const OSTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<OSTemplate[]>([]);
  const [hosts, setHosts] = useState<ProxmoxHost[]>([]);
  const [stats, setStats] = useState<TemplateStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'templates' | 'hosts'>('templates');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OSTemplate | null>(null);
  const [syncingTemplate, setSyncingTemplate] = useState<string | null>(null);
  const [selectedHost, setSelectedHost] = useState<number | null>(null);

  const [formData, setFormData] = useState<CreateOSTemplateData>({
    family: 'ubuntu',
    version: '',
    display_name: '',
    code_name: '',
    description: '',
    logo_url: '/images/os/ubuntu.svg',
    min_cpu: 1,
    min_ram_mb: 512,
    min_disk_gb: 10,
    cloud_image_url: '',
    default_username: 'ubuntu',
    is_active: true,
    is_featured: false,
    sort_order: 100
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesData, hostsData, statsData] = await Promise.all([
        osTemplateService.getAllTemplates(false, true),
        proxmoxHostService.getAllProxmoxHosts(),
        osTemplateService.getTemplateStats()
      ]);
      setTemplates(templatesData);
      setHosts(hostsData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await osTemplateService.createTemplate(formData);
      setSuccess('Template created successfully');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;
    try {
      await osTemplateService.updateTemplate(editingTemplate.id, formData);
      setSuccess('Template updated successfully');
      setEditingTemplate(null);
      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    try {
      await osTemplateService.deleteTemplate(id);
      setSuccess('Template deleted successfully');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleSyncToHost = async (templateId: string, hostId: number) => {
    try {
      setSyncingTemplate(templateId);
      const result = await osTemplateService.syncTemplateToHost(templateId, hostId);
      setSuccess(result.message);
      // Refresh after a delay to show updated status
      setTimeout(fetchData, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start sync');
    } finally {
      setSyncingTemplate(null);
    }
  };

  const handleSyncToAllHosts = async (templateId: string) => {
    try {
      setSyncingTemplate(templateId);
      const result = await osTemplateService.syncTemplateToAllHosts(templateId);
      setSuccess(result.message);
      setTimeout(fetchData, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start sync');
    } finally {
      setSyncingTemplate(null);
    }
  };

  const handleSyncAllToHost = async (hostId: number) => {
    try {
      const result = await osTemplateService.syncAllTemplatesToHost(hostId);
      setSuccess(result.message);
      setTimeout(fetchData, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start sync');
    }
  };

  const openEditModal = (template: OSTemplate) => {
    setEditingTemplate(template);
    setFormData({
      family: template.family,
      version: template.version,
      display_name: template.display_name,
      code_name: template.code_name || '',
      description: template.description || '',
      logo_url: template.logo_url || '',
      min_cpu: template.min_cpu,
      min_ram_mb: template.min_ram_mb,
      min_disk_gb: template.min_disk_gb,
      cloud_image_url: template.cloud_image_url,
      default_username: template.default_username,
      is_active: template.is_active,
      is_featured: template.is_featured,
      sort_order: template.sort_order
    });
  };

  const resetForm = () => {
    setFormData({
      family: 'ubuntu',
      version: '',
      display_name: '',
      code_name: '',
      description: '',
      logo_url: '/images/os/ubuntu.svg',
      min_cpu: 1,
      min_ram_mb: 512,
      min_disk_gb: 10,
      cloud_image_url: '',
      default_username: 'ubuntu',
      is_active: true,
      is_featured: false,
      sort_order: 100
    });
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      ready: 'bg-green-100 text-green-800',
      downloading: 'bg-blue-100 text-blue-800',
      creating: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800',
      outdated: 'bg-yellow-100 text-yellow-800'
    };
    const icons: Record<string, string> = {
      ready: '✅',
      downloading: '⬇️',
      creating: '🔧',
      pending: '⏳',
      failed: '❌',
      outdated: '🔄'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {icons[status] || '❓'} {status}
      </span>
    );
  };

  const familyLogos: Record<string, string> = {
    ubuntu: '🟠',
    debian: '🔴',
    almalinux: '🔵',
    rocky: '🟢',
    fedora: '🔷',
    centos: '🟣'
  };

  // Group templates by family
  const templatesByFamily = templates.reduce((acc, template) => {
    if (!acc[template.family]) {
      acc[template.family] = [];
    }
    acc[template.family].push(template);
    return acc;
  }, {} as Record<string, OSTemplate[]>);

  const FormModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <h2 className="text-2xl font-bold">
            {editingTemplate ? '📝 Edit OS Template' : '➕ Add OS Template'}
          </h2>
        </div>

        <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OS Family *</label>
              <select
                value={formData.family}
                onChange={(e) => setFormData(prev => ({ ...prev, family: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="ubuntu">Ubuntu</option>
                <option value="debian">Debian</option>
                <option value="almalinux">AlmaLinux</option>
                <option value="rocky">Rocky Linux</option>
                <option value="fedora">Fedora</option>
                <option value="centos">CentOS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version *</label>
              <input
                type="text"
                required
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 22.04"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
              <input
                type="text"
                required
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Ubuntu 22.04 LTS"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code Name</label>
              <input
                type="text"
                value={formData.code_name}
                onChange={(e) => setFormData(prev => ({ ...prev, code_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Jammy Jellyfish"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Username *</label>
              <input
                type="text"
                required
                value={formData.default_username}
                onChange={(e) => setFormData(prev => ({ ...prev, default_username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., ubuntu"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Cloud Image URL *</label>
              <input
                type="url"
                required
                value={formData.cloud_image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, cloud_image_url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="https://cloud-images.ubuntu.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min CPU</label>
              <input
                type="number"
                min="1"
                value={formData.min_cpu}
                onChange={(e) => setFormData(prev => ({ ...prev, min_cpu: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min RAM (MB)</label>
              <input
                type="number"
                min="256"
                step="256"
                value={formData.min_ram_mb}
                onChange={(e) => setFormData(prev => ({ ...prev, min_ram_mb: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Disk (GB)</label>
              <input
                type="number"
                min="5"
                value={formData.min_disk_gb}
                onChange={(e) => setFormData(prev => ({ ...prev, min_disk_gb: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="col-span-2 flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm">Featured (Recommended)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => { setShowAddModal(false); setEditingTemplate(null); resetForm(); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <p className="text-green-700 font-medium">{success}</p>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center text-white">
          <div>
            <h2 className="text-3xl font-bold mb-1">💿 OS Template Management</h2>
            <p className="text-purple-100">Manage operating system templates for VPS provisioning</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 font-bold shadow-xl"
          >
            ➕ Add Template
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Total Templates</p>
            <p className="text-2xl font-bold text-gray-900">{stats.totalTemplates}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Active Templates</p>
            <p className="text-2xl font-bold text-green-600">{stats.activeTemplates}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Ready on Hosts</p>
            <p className="text-2xl font-bold text-blue-600">{stats.readyHostTemplates}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Pending/Failed</p>
            <p className="text-2xl font-bold text-orange-600">
              {stats.pendingHostTemplates} / {stats.failedHostTemplates}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Templates
          </button>
          <button
            onClick={() => setActiveTab('hosts')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'hosts'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🖥️ Host Status
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <p className="mt-4 text-gray-600">Loading templates...</p>
        </div>
      ) : activeTab === 'templates' ? (
        /* Templates Tab */
        <div className="space-y-6">
          {Object.entries(templatesByFamily).map(([family, familyTemplates]) => (
            <div key={family} className="bg-white rounded-lg shadow-sm border">
              <div className="bg-gray-50 px-6 py-3 border-b flex items-center gap-2">
                <span className="text-2xl">{familyLogos[family] || '🐧'}</span>
                <h3 className="text-lg font-semibold capitalize">{family}</h3>
                <span className="text-sm text-gray-500">({familyTemplates.length} versions)</span>
              </div>
              <div className="divide-y">
                {familyTemplates.map(template => (
                  <div key={template.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium text-gray-900">{template.display_name}</h4>
                          {template.is_featured && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                              ⭐ Featured
                            </span>
                          )}
                          {!template.is_active && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        {template.code_name && (
                          <p className="text-sm text-gray-500">{template.code_name}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Min: {template.min_cpu} CPU, {template.min_ram_mb}MB RAM, {template.min_disk_gb}GB disk
                          • User: {template.default_username}
                        </p>
                      </div>

                      {/* Host Status Pills */}
                      <div className="flex items-center gap-2 mx-4">
                        {template.host_statuses && template.host_statuses.length > 0 ? (
                          template.host_statuses.map(hs => (
                            <div key={hs.host_id} className="text-center">
                              {getStatusBadge(hs.status)}
                              <p className="text-xs text-gray-400 mt-1">{hs.host_name}</p>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">Not synced to any host</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSyncToAllHosts(template.id)}
                          disabled={syncingTemplate === template.id}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 disabled:opacity-50"
                        >
                          {syncingTemplate === template.id ? '⏳' : '🔄'} Sync All
                        </button>
                        <button
                          onClick={() => openEditModal(template)}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id, template.display_name)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-xl mb-2">No templates configured</p>
              <p className="text-sm">Click "Add Template" to create your first OS template</p>
            </div>
          )}
        </div>
      ) : (
        /* Hosts Tab */
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Template Status by Host</h3>
              {selectedHost && (
                <button
                  onClick={() => handleSyncAllToHost(selectedHost)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  🔄 Sync All Templates to Selected Host
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Template
                    </th>
                    {hosts.filter(h => h.status === 'active').map(host => (
                      <th
                        key={host.id}
                        className={`px-4 py-3 text-center text-xs font-medium uppercase cursor-pointer ${
                          selectedHost === host.id ? 'text-purple-600 bg-purple-50' : 'text-gray-500'
                        }`}
                        onClick={() => setSelectedHost(selectedHost === host.id ? null : host.id)}
                      >
                        {host.name}
                        <div className="text-[10px] font-normal normal-case text-gray-400">
                          {host.datacenter_location}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {templates.filter(t => t.is_active).map(template => (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>{familyLogos[template.family] || '🐧'}</span>
                          <span className="font-medium">{template.display_name}</span>
                        </div>
                      </td>
                      {hosts.filter(h => h.status === 'active').map(host => {
                        const hostStatus = template.host_statuses?.find(
                          hs => hs.host_id === host.id
                        );
                        return (
                          <td key={host.id} className="px-4 py-3 text-center">
                            {hostStatus ? (
                              <div>
                                {getStatusBadge(hostStatus.status)}
                                {hostStatus.status !== 'ready' && hostStatus.status !== 'downloading' && hostStatus.status !== 'creating' && (
                                  <button
                                    onClick={() => handleSyncToHost(template.id, host.id)}
                                    disabled={syncingTemplate === template.id}
                                    className="block mx-auto mt-1 text-xs text-blue-600 hover:underline"
                                  >
                                    Sync
                                  </button>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSyncToHost(template.id, host.id)}
                                disabled={syncingTemplate === template.id}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                ➕ Add
                              </button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {(showAddModal || editingTemplate) && <FormModal />}
    </div>
  );
};

export default OSTemplateManagement;
